import { useStrings } from "@1js/localization-components";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import type { ContentTags, VideoPostMessage } from "../../types";
import { videoEventNameToBehavior } from "../../utils/Telemetry/constants";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { CloseTooltip } from "../PromptDetail/PromptDescription.web.string";
import VideoPlayer from "./UMP/VideoPlayer";
import type { Video } from "./VideoCarousel.web.data";

const useStyles = makeStyles({
  root: {
    color: "#363636",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("16px"),
    backgroundColor: "#fff",
    maxHeight: "100vh",
    maxWidth: "75vw",
  },

  oldRoot: {
    color: "#363636",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("16px"),
    backgroundColor: "#fff",

    height: "95vh",
    maxWidth: "95vw",
  },

  dialogBody: {
    height: "100%",
  },

  dialogContent: {
    ...shorthands.overflow("hidden"),
  },

  iframe: {
    height: "100%",
    width: "100%",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("16px"),
  },
});

const sanitize = (value: string) => {
  return value.replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

const populateContentTags = (
  title: string,
  videoId: string,
  duration: number,
  currTime: number
) => {
  let vidPercent = 0;
  if (duration > 0) {
    vidPercent = Math.round(100 * (currTime / duration));
  }
  const contentTags: ContentTags = {
    vidnm: title,
    vidid: videoId,
    viddur: Math.round(duration),
    vidwt: Math.round(currTime),
    vidtimeseconds: Math.round(duration),
    id: `videoMetaData_${videoId}`,
    vidpct: vidPercent,
    live: false,
    containerName: "oneplayer",
  };

  return contentTags;
};

type Props = {
  open: boolean;
  onClosed: () => void;
  video?: Video;
  locale: string;
};

export const VideoPlayerDialog: React.FC<Props> = ({
  open,
  onClosed,
  video,
  locale,
}) => {
  const styles = useStyles();
  const telemetry = useTelemetry();
  const { isFlightActive } = useFlightContext();
  const localizations = useStrings({
    CloseTooltip,
  });

  React.useEffect(() => {
    const sendMessageEvent = (event: MessageEvent) => {
      let message: VideoPostMessage;
      try {
        message = JSON.parse(sanitize(event.data));
      } catch (err) {
        return;
      }
      if (!message || typeof message !== "object" || !message.eventName) {
        return;
      }

      const eventName = message.eventName.toLowerCase();

      const contentTags = populateContentTags(
        video?.title ?? "",
        video?.id ?? "",
        message.data?.endTime ?? 0,
        message.data?.currentTime ?? 0
      );
      const pageOverride = {
        actionType: "O",
        behavior: videoEventNameToBehavior.get(eventName),
        content: JSON.stringify(contentTags),
        isAuto: false,
      };
      telemetry.sendVideoTelemetryEvent(pageOverride);
    };

    window?.addEventListener("message", sendMessageEvent);
    return () => {
      window?.removeEventListener("message", sendMessageEvent);
    };
  }, [video, telemetry]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClosed();
        }
      }}
    >
      {isFlightActive("isUMPVideoEnabled") ? (
        <DialogSurface className={styles.root}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label={localizations.CloseTooltip()}
                    icon={<Dismiss24Regular />}
                  />
                </DialogTrigger>
              }
            ></DialogTitle>
            <DialogContent>
              <VideoPlayer
                videoId={video?.id || ""}
                videoTitle={video?.title || ""}
                locale={locale}
                isLearnVideo={video?.isLearnVideo || false}
                highQualityVideoUrl={video?.highQualityVideoUrl || ""}
                mediumQualityVideoUrl={video?.mediumQualityVideoUrl || ""}
                lowQualityVideoUrl={video?.lowQualityVideoUrl || ""}
                adaptiveVideoDashUrl={video?.adaptiveVideoDashUrl || ""}
                adaptiveVideoHLSUrl={video?.adaptiveVideoHLSUrl || ""}
                poster={video?.poster || ""}
                captions={video?.captions || ""}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      ) : (
        <DialogSurface className={styles.oldRoot}>
          <DialogBody className={styles.dialogBody}>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label={localizations.CloseTooltip()}
                    icon={<Dismiss24Regular />}
                  />
                </DialogTrigger>
              }
            ></DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <iframe
                title={video?.title || ""}
                src={video?.url || ""}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className={styles.iframe}
                sandbox="allow-scripts allow-popups"
              ></iframe>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      )}
    </Dialog>
  );
};
